import { bytesToReadableFormat, bytesToGiB } from '@/react/kubernetes/utils';

import { FormControl } from '@@/form-components/FormControl';
import { Option, PortainerSelect } from '@@/form-components/PortainerSelect';
import { Input } from '@@/form-components/Input';
import { TextTip } from '@@/Tip/TextTip';
import { Switch } from '@@/form-components/SwitchField/Switch';

import { BlockDevice, OmniMachineFormValues } from '../types';

// bytesToGiB returns the binary value (base-2), which we label as "GB"
// to match the convention used by VMware and most virtualization tools.
const bytesToGB = bytesToGiB;

export interface DeviceSettingsFormProps {
  index: number;
  blockDevices: BlockDevice[];
  machineFormValues: OmniMachineFormValues;
  onChange: (machineFormValues: OmniMachineFormValues, index: number) => void;
}

export function DeviceSettingsForm({
  index,
  blockDevices,
  machineFormValues,
  onChange,
}: DeviceSettingsFormProps) {
  const diskOptions: Option<string>[] = blockDevices
    .filter((device) => !device.readonly)
    .map((device) => ({
      label: formatDiskLabel(device),
      value: device.linux_name ?? '',
    }));

  const selectedDevice = blockDevices.find(
    (d) => d.linux_name === machineFormValues.disk
  );
  const totalDiskGB = bytesToGB(selectedDevice?.size);
  // Talos reserves ~2-3 GB for system partitions (EFI, STATE, META).
  // Round up to 3 GB to provide a safe margin across Talos versions.
  const talosOverheadGB = 3;
  const usableDiskGB = Math.max(0, totalDiskGB - talosOverheadGB);
  const hasSystemDiskSize =
    machineFormValues.systemDiskSize !== undefined &&
    machineFormValues.systemDiskSize > 0;

  const systemDiskSizeError = getSystemDiskSizeError(
    hasSystemDiskSize,
    machineFormValues.systemDiskSize,
    usableDiskGB
  );

  const noRemainingSpace =
    usableDiskGB > 0 &&
    machineFormValues.systemDiskSize !== undefined &&
    machineFormValues.systemDiskSize >= usableDiskGB;

  const remainingDiskGB =
    usableDiskGB > 0 && machineFormValues.systemDiskSize
      ? Math.max(0, usableDiskGB - machineFormValues.systemDiskSize)
      : 0;

  const hasUserDiskSize =
    !!machineFormValues.enableUserDisk &&
    machineFormValues.userDiskSize !== undefined &&
    machineFormValues.userDiskSize > 0;

  const userDiskSizeError = getUserDiskSizeError(
    machineFormValues.enableUserDisk ?? false,
    hasUserDiskSize,
    machineFormValues.userDiskSize,
    remainingDiskGB
  );

  return (
    <div className="form-horizontal">
      <FormControl
        label="Install disk"
        tooltip="Select the block device where Talos will be installed"
        size="large"
      >
        <PortainerSelect<string>
          value={machineFormValues.disk ?? ''}
          options={diskOptions}
          onChange={(disk) => {
            if (!disk) return;
            const newConfig = structuredClone(machineFormValues);
            newConfig.disk = disk;
            onChange(newConfig, index);
          }}
          data-cy="omniConfigureMachine-diskSelect"
        />
      </FormControl>
      {machineFormValues.disk && (
        <>
          <TextTip color="blue" className="mb-2">
            The EPHEMERAL system volume stores container data, images, logs, and
            etcd data. By default it uses the entire disk. Set a size limit to
            reserve space for a user volume. See the{' '}
            <a
              target="_blank"
              href="https://docs.siderolabs.com/talos/latest/configure-your-talos-cluster/storage-and-disk-management/disk-management/system"
              rel="noreferrer"
            >
              System Volumes
            </a>{' '}
            docs for more information.
          </TextTip>
          <FormControl
            label="System disk size (GB)"
            tooltip={`Limits the Talos EPHEMERAL system partition size. Recommended minimum 40 GB${
              usableDiskGB ? ` (~${usableDiskGB} GB usable)` : ''
            }.`}
            size="large"
            errors={systemDiskSizeError}
            required
          >
            <Input
              type="number"
              min={20}
              max={usableDiskGB || undefined}
              placeholder={
                usableDiskGB ? `~${usableDiskGB} GB available` : 'e.g. 40'
              }
              value={machineFormValues.systemDiskSize ?? ''}
              onChange={(e) => {
                const newConfig = structuredClone(machineFormValues);
                const val = e.target.value;
                newConfig.systemDiskSize = val ? Number(val) : undefined;
                if (!newConfig.systemDiskSize) {
                  newConfig.enableUserDisk = false;
                }
                onChange(newConfig, index);
              }}
              data-cy="omniConfigureMachine-systemDiskSizeInput"
            />
          </FormControl>
          {hasSystemDiskSize && (
            <>
              <FormControl label="Enable user volume" size="large">
                <div className="flex h-[34px] items-center">
                  <Switch
                    id="omniConfigureMachine-enableUserDisk"
                    name="omniConfigureMachine-enableUserDisk"
                    checked={
                      noRemainingSpace
                        ? false
                        : (machineFormValues.enableUserDisk ?? false)
                    }
                    disabled={noRemainingSpace}
                    onChange={(enabled) => {
                      const newConfig = structuredClone(machineFormValues);
                      newConfig.enableUserDisk = enabled;
                      if (enabled && !newConfig.userDiskVolumeName) {
                        newConfig.userDiskVolumeName = 'data';
                      }
                      onChange(newConfig, index);
                    }}
                    data-cy="omniConfigureMachine-enableUserDiskSwitch"
                  />
                </div>
              </FormControl>
              {noRemainingSpace && (
                <TextTip
                  color="orange"
                  inline={false}
                  className="mb-2 items-center"
                >
                  No remaining disk space. Reduce the system disk size to enable
                  a user volume.
                </TextTip>
              )}
              {machineFormValues.enableUserDisk && (
                <>
                  <TextTip color="blue" className="mb-2">
                    User volumes allocate remaining disk space for Kubernetes
                    workloads via <code>hostPath</code> mounts. The volume is
                    identified by a unique name (1-34 chars, lowercase
                    alphanumeric and dashes) and automatically mounted at{' '}
                    <code>/var/mnt/&lt;volume-name&gt;</code>. See the{' '}
                    <a
                      target="_blank"
                      href="https://docs.siderolabs.com/talos/latest/configure-your-talos-cluster/storage-and-disk-management/disk-management/user"
                      rel="noreferrer"
                    >
                      User Volumes
                    </a>{' '}
                    docs for more information.
                  </TextTip>
                  <FormControl
                    label="Volume name"
                    tooltip="The name of the user volume. The volume will be mounted at /var/mnt/<volume-name> automatically."
                    size="large"
                  >
                    <Input
                      value={machineFormValues.userDiskVolumeName ?? 'data'}
                      maxLength={34}
                      onChange={(e) => {
                        const newConfig = structuredClone(machineFormValues);
                        newConfig.userDiskVolumeName = e.target.value;
                        onChange(newConfig, index);
                      }}
                      data-cy="omniConfigureMachine-userDiskVolumeNameInput"
                    />
                  </FormControl>
                  <FormControl
                    label="Volume size (GB)"
                    tooltip={`Size for the user volume${
                      remainingDiskGB
                        ? ` (~${remainingDiskGB} GB remaining)`
                        : ''
                    }.`}
                    size="large"
                    errors={userDiskSizeError}
                    required
                  >
                    <Input
                      type="number"
                      min={1}
                      max={remainingDiskGB || undefined}
                      placeholder={
                        remainingDiskGB
                          ? `~${remainingDiskGB} GB remaining`
                          : 'e.g. 10'
                      }
                      value={machineFormValues.userDiskSize ?? ''}
                      onChange={(e) => {
                        const newConfig = structuredClone(machineFormValues);
                        const val = e.target.value;
                        newConfig.userDiskSize = val ? Number(val) : undefined;
                        onChange(newConfig, index);
                      }}
                      data-cy="omniConfigureMachine-userDiskSizeInput"
                    />
                  </FormControl>
                  <TextTip
                    color="blue"
                    inline={false}
                    className="mb-2 items-center"
                  >
                    Mount path:{' '}
                    <code>
                      /var/mnt/
                      {machineFormValues.userDiskVolumeName || 'data'}
                    </code>
                  </TextTip>
                </>
              )}
            </>
          )}
        </>
      )}
    </div>
  );
}

function getSystemDiskSizeError(
  hasSystemDiskSize: boolean,
  systemDiskSize: number | undefined,
  usableDiskGB: number
): string | undefined {
  if (!hasSystemDiskSize) {
    return 'System disk size is required';
  }
  if (
    systemDiskSize !== undefined &&
    usableDiskGB > 0 &&
    systemDiskSize > usableDiskGB
  ) {
    return `System disk size cannot exceed the usable disk space (~${usableDiskGB} GB after Talos system partitions)`;
  }
  return undefined;
}

function getUserDiskSizeError(
  enableUserDisk: boolean | undefined,
  hasUserDiskSize: boolean,
  userDiskSize: number | undefined,
  remainingDiskGB: number
): string | undefined {
  if (!enableUserDisk) {
    return undefined;
  }
  if (!hasUserDiskSize) {
    return 'User volume size is required';
  }
  if (remainingDiskGB > 0 && userDiskSize! > remainingDiskGB) {
    return `Volume size cannot exceed the remaining disk space (~${remainingDiskGB} GB)`;
  }
  return undefined;
}

function formatDiskSize(bytes?: number): string {
  if (!bytes) return '';
  return bytesToReadableFormat(bytes, {
    output: 'string',
    standard: 'jedec',
  }) as string;
}

function formatDiskLabel(device: BlockDevice): string {
  const parts = [device.linux_name];
  if (device.size) parts.push(formatDiskSize(device.size));
  if (device.type) parts.push(device.type);
  if (device.system_disk) parts.push('(system)');
  if (device.readonly) parts.push('(readonly)');
  return parts.filter(Boolean).join(' - ');
}
